// Helpers shared across the operation dashboard.

export const ACTION_STATUSES = [
  "pending",
  "inprogress",
  "active",
  "completed",
  "rejected",
  "canceled",
];

// Date of the latest status change, falling back to update/creation time.
export const getActionDate = (action) => {
  const history = action?.statusHistory;
  const lastHistory = history?.[history.length - 1];
  return lastHistory?.changedAt || action?.updatedAt || action?.createdAt || "";
};

// Month-to-date through yesterday, based on yesterday's month so the range is
// never inverted on the 1st. The monthly view defaults to the previous month
// (1-based, as the backend expects), rolling over to December in January.
export const getDefaultFilters = (now = new Date()) => {
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return {
    store: [],
    app: [],
    from: new Date(yesterday.getFullYear(), yesterday.getMonth(), 1),
    to: yesterday,
    MonthlyDate: {
      month: previousMonth.getMonth() + 1,
      year: previousMonth.getFullYear(),
    },
  };
};

// "yyyy-mm" from an <input type="month">, or null when cleared/invalid.
export const parseMonthInput = (value) => {
  const [year, month] = String(value || "").split("-").map(Number);
  if (!year || !month) return null;
  return { year, month };
};

// Longest campaign name a user can type (the server enforces the same limit).
// Names of existing catalog campaigns are never held to it.
export const MAX_CAMPAIGN_NAME = 200;

// Campaign fields can still be edited in these states (server-enforced): the two pending review steps.
export const EDITABLE_STATUSES = ["pending"];

// Campaign types that can be created. "promoCamp" is legacy: it is only ever
// displayed (see campaignTypeLabel), never offered.
export const getCampaignTypeOptions = (lang) => [
  {
    id: "deliveryCamp",
    value: "deliveryCamp",
    label: lang === "ar" ? "توصيل" : "Delivery",
  },
  {
    id: "paidCamp",
    value: "paidCamp",
    label: lang === "ar" ? "مدفوعة" : "Paid",
  },
];

const CAMPAIGN_TYPE_LABELS = {
  deliveryCamp: { ar: "حملة توصيل", en: "Delivery Campaign" },
  paidCamp: { ar: "حملة مدفوعة", en: "Paid Campaign" },
  promoCamp: { ar: "حملة ترويجية (قديمة)", en: "Promotional Campaign (legacy)" },
};

// Label for any stored campaign type, including legacy records.
export const campaignTypeLabel = (type, lang) =>
  CAMPAIGN_TYPE_LABELS[type]?.[lang === "ar" ? "ar" : "en"] || type || "";

const ACTION_TYPE_LABELS = {
  campaigns: { ar: "حملات", en: "Campaigns" },
  menu: { ar: "قائمة طعام (قديم)", en: "Menu (legacy)" },
  general: { ar: "عام (قديم)", en: "General (legacy)" },
};

export const actionTypeLabel = (type, lang) =>
  ACTION_TYPE_LABELS[type]?.[lang === "ar" ? "ar" : "en"] || type || "";

// Catalog entries (both creatable types) as picker items.
export const toCatalogItems = (campaignData, lang) =>
  ["deliveryCamp", "paidCamp"].flatMap((campaignType) =>
    (campaignData?.[campaignType] || []).map((entry) => ({
      id: String(entry._id),
      title: (lang === "ar" ? entry.ar || entry.en : entry.en || entry.ar) || "",
      subtitle: (lang === "ar" ? entry.en : entry.ar) || "",
      type: campaignType,
    })),
  );

// A campaign action as a picker item. Fields come straight from the record.
export const actionToCampaignItem = (action, storeNames = {}) => ({
  id: action._id,
  title: action.campaignName || String(action.content || "").split("\n")[0].trim(),
  subtitle: action.campaignDetails || "",
  type: action.campaignType,
  status: action.status,
  appName: action.appName,
  storeId: action.storeId,
  storeName: storeNames[action.storeId] || "",
  startDate: action.startDate,
  endDate: action.endDate,
  deletedAt: action.deletedAt,
  origin: action.origin,
  action,
});

// Dark or light text for a solid hex background (WCAG relative luminance).
// Non-hex values (e.g. CSS variables) fall back to the theme text colour.
export const getReadableTextColor = (background) => {
  const match = /^#([0-9a-f]{6})$/i.exec(String(background || "").trim());
  if (!match) return "var(--ui-text-primary)";
  const channels = [0, 2, 4].map((i) => {
    const c = parseInt(match[1].slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
  // Contrast against white vs. near-black (#1a1a1a, luminance ≈ 0.0103).
  const contrastWithWhite = 1.05 / (luminance + 0.05);
  const contrastWithDark = (luminance + 0.05) / 0.0603;
  return contrastWithWhite >= contrastWithDark ? "#ffffff" : "#1a1a1a";
};
